import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { ClientPromptItem } from "../AiPromptsTab";

interface Props {
  prompt: ClientPromptItem;
  onClose: () => void;
  onSave: (updatedValue: string) => Promise<void>;
  isLoading: boolean;
}

enum FieldName {
  promptText = "promptText"
}

interface FormValues {
  [FieldName.promptText]: string;
}

const validationSchema = Yup.object().shape({
  [FieldName.promptText]: Yup.string().required("Prompt text is required")
});

const EditClientAiPromptDialog: React.FC<Props> = ({ prompt, onClose, onSave, isLoading }) => {
  const initialValues: FormValues = {
    [FieldName.promptText]: prompt.isOverridden ? prompt.overrideValue || "" : prompt.defaultValue
  };

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await onSave(values[FieldName.promptText]);
    },
    [onSave]
  );

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{prompt.isOverridden ? "Edit AI Prompt Override" : "Create AI Prompt Override"}</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="h6" gutterBottom>
                  {prompt.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {prompt.description}
                </Typography>

                {!prompt.isOverridden && (
                  <Alert severity="info">
                    You are creating a client-level override for this prompt. The system default will no longer be used
                    for this client.
                  </Alert>
                )}

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" fontWeight="medium">
                      View System Default Prompt
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                        whiteSpace: "pre-wrap",
                        backgroundColor: "grey.100",
                        p: 2,
                        borderRadius: 1
                      }}
                    >
                      {prompt.defaultValue}
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <TextInputField
                  name={FieldName.promptText}
                  label="Client Override Prompt Text"
                  helperText="Enter the AI prompt instructions for this client. This will override the system default."
                  variant="outlined"
                  multiline
                  rows={12}
                  required
                  fullWidth
                  autoFocus
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting || isLoading}>
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                isLoading={isSubmitting || isLoading}
                disabled={!isValid || !dirty}
              >
                Save Override
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { EditClientAiPromptDialog };
